import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../lib/cn.js';

const FIELD_BASE =
  'w-full rounded-[9px] border border-line bg-surface px-3.5 py-2.5 text-[13px] font-medium text-ink ' +
  'placeholder:text-ink-faint focus:border-primary focus:outline-none disabled:bg-canvas disabled:text-ink-muted';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(FIELD_BASE, invalid && 'border-danger focus:border-danger', className)}
      {...rest}
    />
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, ...rest },
  ref,
) {
  return (
    <textarea
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        FIELD_BASE,
        'resize-none rounded-[10px] leading-[1.5]',
        invalid && 'border-danger focus:border-danger',
        className,
      )}
      {...rest}
    />
  );
});

export interface FieldProps {
  label: string;
  htmlFor?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  className?: string;
}

/** Label + control + message, so forms stay consistent and accessible. */
export function Field({ label, htmlFor, hint, error, children, className }: FieldProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label htmlFor={htmlFor} className="text-[11px] font-semibold text-ink-faint">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-[11.5px] font-medium text-danger">{error}</p>
      ) : hint ? (
        <p className="text-[11.5px] font-medium text-ink-faint">{hint}</p>
      ) : null}
    </div>
  );
}
